const MAX_RELOAD_ATTEMPTS = 3;
const RELOAD_PARAM = 'asset_reload';
const RELOAD_STORAGE_KEY = 'pli_asset_reload_count';
const RELOAD_TIMESTAMP_KEY = 'pli_asset_reload_timestamp';
const RELOAD_RESET_INTERVAL = 5 * 60 * 1000;
let assetReloadInProgress = false;

const dynamicImportErrorMessages = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'chunkloaderror',
  'loading chunk'
];

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) return `${error.name} ${error.message}`.toLowerCase();
  if (typeof error === 'string') return error.toLowerCase();
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message?: unknown }).message).toLowerCase();
  }

  return '';
};

export const isDynamicImportLoadError = (error: unknown) => {
  const message = getErrorText(error);

  return dynamicImportErrorMessages.some(errorMessage => message.includes(errorMessage));
};

const getReloadAttempts = () => {
  try {
    const timestamp = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? '0');
    const now = Date.now();

    if (now - timestamp > RELOAD_RESET_INTERVAL) {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, '0');
      sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, now.toString());
      return 0;
    }

    return Number(sessionStorage.getItem(RELOAD_STORAGE_KEY) ?? '0');
  } catch {
    return 0;
  }
};

const incrementReloadAttempts = () => {
  try {
    const attempts = getReloadAttempts() + 1;
    sessionStorage.setItem(RELOAD_STORAGE_KEY, attempts.toString());
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, Date.now().toString());
    return attempts;
  } catch {
    return MAX_RELOAD_ATTEMPTS + 1;
  }
};

const getReloadUrl = (attempts: number) => {
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, attempts.toString());
  return url.toString();
};

export const recoverFromAssetLoadError = (reason: string) => {
  if (assetReloadInProgress) return true;

  const reloadAttempts = getReloadAttempts();

  if (reloadAttempts >= MAX_RELOAD_ATTEMPTS) {
    console.error(`DIAG: Asset reload recovery stopped after ${reloadAttempts} attempts.`, reason);
    return false;
  }

  const attempts = incrementReloadAttempts();
  if (attempts > MAX_RELOAD_ATTEMPTS) return false;

  assetReloadInProgress = true;

  const reloadUrl = getReloadUrl(attempts);
  console.info(
    `DIAG: Reloading after asset load failure (${attempts}/${MAX_RELOAD_ATTEMPTS})`,
    reason
  );

  if ('serviceWorker' in navigator) {
    let didReload = false;
    const reload = () => {
      if (didReload) return;
      didReload = true;
      window.location.replace(reloadUrl);
    };

    window.setTimeout(reload, 1500);
    navigator.serviceWorker
      .getRegistration()
      .then(registration => registration?.unregister())
      .finally(reload);
  } else {
    window.location.replace(reloadUrl);
  }

  return true;
};
